/**
 * ConfluenceListItem Component
 *
 * Displays a single confluence item row with:
 * - Name
 * - Category badge (color swatch + name)
 * - "Used in" count with polarity popover
 * - Action menu (Edit, Archive/Restore)
 */

import React from 'react';
import { MoreVertical } from 'lucide-react';

import {
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@enfomentis/ui';
import type { Confluence } from '@/types/schema/Confluence';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';

import { ConfluenceUsedInPopover } from './ConfluenceUsedInPopover';
import type { ConfluenceUsedInData } from './ConfluenceUsedInPopover';

export interface ConfluenceListItemProps {
  /** The confluence item to display */
  confluence: Confluence;
  /** The category this confluence belongs to (if any) */
  category: MarketConditionCategory | undefined;
  /** Array of playbook usage data with polarity */
  usedInData: ConfluenceUsedInData[];
  /** Handler for edit action */
  onEdit: () => void;
  /** Handler for archive click (opens confirmation in parent) */
  onArchiveClick: () => void;
  /** Handler for restore action */
  onRestore: () => void;
}

export const ConfluenceListItem: React.FC<ConfluenceListItemProps> = ({
  confluence,
  category,
  usedInData,
  onEdit,
  onArchiveClick,
  onRestore,
}) => {
  const isArchived = confluence.status === 'archived';

  return (
    <div
      className={`flex items-center gap-3 p-3 ${
        isArchived ? 'opacity-60 bg-default-50' : 'bg-content1'
      }`}
    >
      {/* Name + Archived Badge */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center min-w-0">
          <span
            className={`font-medium truncate ${
              isArchived ? 'text-text-tertiary' : 'text-text-primary'
            }`}
          >
            {confluence.name}
          </span>
          {isArchived && (
            <Chip className="ml-2 shrink-0 text-xs text-text-tertiary bg-default-100">
              Archived
            </Chip>
          )}
        </div>
        {confluence.description && (
          <span className="text-sm text-text-secondary truncate block">
            {confluence.description}
          </span>
        )}
      </div>

      {/* Category Badge */}
      <div className="shrink-0">
        {category ? (
          <Chip
            size="sm"
            variant="flat"
            className="text-xs"
            style={{ backgroundColor: `${category.color}20`, color: category.color }}
            startContent={
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: category.color }}
              />
            }
          >
            {category.name}
          </Chip>
        ) : (
          <span className="text-xs text-text-tertiary">No category</span>
        )}
      </div>

      {/* Used In Count with Polarity */}
      <div className="shrink-0">
        <ConfluenceUsedInPopover usedInData={usedInData} />
      </div>

      {/* Actions Menu */}
      <div className="shrink-0">
        <Dropdown>
          <DropdownTrigger>
            <Button
              size="sm"
              variant="light"
              isIconOnly
              aria-label="Confluence actions"
            >
              <MoreVertical size={16} />
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Confluence actions">
            <DropdownItem key="edit" onPress={onEdit}>
              Edit
            </DropdownItem>
            {isArchived ? (
              <DropdownItem key="restore" onPress={onRestore}>
                Restore
              </DropdownItem>
            ) : (
              <DropdownItem
                key="archive"
                onPress={onArchiveClick}
                className="text-warning-600"
              >
                Archive
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
};
